// ============================================
// Products Hooks - TanStack Query
// ============================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import type {
  Product,
  ProductStatus,
  CreateProductRequest,
  UpdateProductRequest,
  PaginationParams,
  ProductListParams,
  ProductStats,
  ProductTranslation,
  BulkUpdateProductsRequest,
} from '@conai/shared-types';
import { useProductsService } from '../providers/ApiProvider';
import { queryKeys } from '../config/query-client';
import { useI18n } from '../i18n';

// Parse Django ErrorDetail format
function parseBackendError(raw: string): string[] {
  const lines: string[] = [];
  const fieldRegex = /'([^']+)':\s*\[([^\]]+)\]/g;
  for (const fieldMatch of raw.matchAll(fieldRegex)) {
    const field = fieldMatch[1];
    const msgRegex = /ErrorDetail\(string='([^']+)'/g;
    for (const msgMatch of fieldMatch[2].matchAll(msgRegex)) {
      lines.push(`${field}: ${msgMatch[1]}`);
    }
  }
  if (lines.length > 0) return lines;
  const detailRegex = /ErrorDetail\(string='([^']+)'/g;
  for (const m of raw.matchAll(detailRegex)) lines.push(m[1]);
  return lines.length > 0 ? lines : [raw];
}

interface ProductFilters extends PaginationParams {
  category_id?: string;
  categoryId?: string;
  status?: ProductStatus;
  search?: string;
  store?: string;
  storeId?: string;
  price_min?: number;
  price_max?: number;
  ordering?: string;
}

function normalizeProductFilters(filters: ProductFilters): ProductListParams {
  return {
    ...filters,
    category_id: filters.category_id ?? filters.categoryId,
    store: filters.store ?? filters.storeId,
  };
}

/**
 * Hook to list products with pagination and filters
 */
export function useProducts(filters: ProductFilters = {}) {
  const productsService = useProductsService();
  const normalizedFilters = normalizeProductFilters(filters);

  return useQuery({
    queryKey: queryKeys.products.list(normalizedFilters as unknown as Record<string, unknown>),
    queryFn: () => productsService.list(normalizedFilters),
  });
}

/**
 * Hook to get product stats
 */
export function useProductsStats() {
  const productsService = useProductsService();

  return useQuery<ProductStats>({
    queryKey: [...queryKeys.products.all, 'stats'] as const,
    queryFn: () => productsService.getStats(),
  });
}

/**
 * Hook to get a single product by ID
 */
export function useProduct(id: string, options?: { enabled?: boolean }) {
  const productsService = useProductsService();

  return useQuery({
    queryKey: queryKeys.products.detail(id),
    queryFn: async () => {
      const result = await productsService.getById(id);
      console.group('═══ GET PRODUCT DETAIL ═══');
      console.log('id:', id);
      console.log('response:', result);
      console.log('price:', (result as unknown as Record<string,unknown>).price);
      console.log('base_price:', (result as unknown as Record<string,unknown>).base_price);
      console.log('category:', (result as unknown as Record<string,unknown>).category);
      console.log('supplier:', (result as unknown as Record<string,unknown>).supplier);
      console.groupEnd();
      return result;
    },
    enabled: options?.enabled !== false && !!id,
  });
}

/**
 * Hook to get a product by barcode
 */
export function useProductByBarcode(barcode: string, options?: { enabled?: boolean }) {
  const productsService = useProductsService();

  return useQuery({
    queryKey: queryKeys.products.byBarcode(barcode),
    queryFn: () => productsService.getByBarcode(barcode),
    enabled: options?.enabled !== false && !!barcode,
    retry: false, // Don't retry on barcode not found
  });
}

/**
 * Hook to create a new product
 *
 * GHI LUÔN BẢN DỊCH CỦA NGÔN NGỮ ĐANG TẠO. Người vận hành gõ tên bằng đúng
 * ngôn ngữ giao diện họ đang dùng, nên ngôn ngữ đó là ngôn ngữ gốc của sản
 * phẩm — và đó là thứ CHỈ lúc này mới biết được. Không ghi lại ngay thì về sau
 * không còn cách nào suy ra: cột `name` không mang thông tin nó đang là tiếng
 * gì, và màn nhập bản dịch buộc phải đoán (bản trước đoán bằng ngôn ngữ giao
 * diện lúc MỞ modal — sai ngay khi người mở không phải người tạo).
 *
 * Lỗi ở bước này KHÔNG làm hỏng việc tạo sản phẩm: sản phẩm đã tồn tại rồi,
 * bản dịch chỉ là tiện ích, và người dùng vẫn nhập tay được ở màn bản dịch.
 */
export function useCreateProduct() {
  const productsService = useProductsService();
  const queryClient = useQueryClient();
  const { t, language } = useI18n();

  return useMutation({
    mutationFn: (data: CreateProductRequest) => productsService.create(data),
    onSuccess: async (newProduct) => {
      const raw = newProduct as unknown as Record<string, unknown>;
      const shortDescription =
        typeof raw.short_description === 'string' && raw.short_description.trim()
          ? (raw.short_description as string)
          : null;

      if (newProduct.id && newProduct.name?.trim()) {
        try {
          await productsService.upsertTranslations(newProduct.id, [
            {
              language_code: language,
              name: newProduct.name.trim(),
              short_description: shortDescription,
              unit_label: null,
            },
          ]);
        } catch {
          // Cố tình nuốt: xem ghi chú ở đầu hook. Báo lỗi ở đây chỉ khiến
          // người dùng tưởng sản phẩm chưa được tạo.
        }
      }

      // Invalidate products list
      queryClient.invalidateQueries({ queryKey: queryKeys.products.lists() });
      // Also invalidate inventory if product affects it
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
      toast.success(t('toastProductCreated', { name: newProduct.name }));
    },
    onError: (error: unknown) => {
      const raw = (error as { originalError?: { response?: { data?: { message?: string } } } })
        ?.originalError?.response?.data?.message;
      const lines = raw ? parseBackendError(raw) : [(error as { message?: string })?.message ?? t('failedToCreateProduct')];
      lines.forEach((line) => toast.error(line, { duration: 5000 }));
    },
  });
}

/**
 * Tạo sản phẩm hàng loạt — POST /products/bulk/ (sheet 2 mục #13/#21).
 *
 * ALL-OR-NOTHING. BE bọc cả lô trong một transaction: dòng nào hỏng validation
 * là rollback toàn bộ và trả 400, nên `onSuccess` ở đây thật sự có nghĩa là
 * mọi dòng đã vào — khác hẳn `useBulkAdjust` bên tồn kho (luôn 200, phải tự
 * đọc `summary.failed`). Đừng bê logic đếm thành/bại từ bên đó sang.
 *
 * Lỗi field-level của DRF nằm trong body 400 nên đi qua `parseBackendError`
 * giống `useCreateProduct`, thay vì chỉ hiện `error.message` chung chung.
 */
/**
 * Gán một danh mục cho nhiều sản phẩm cùng lúc.
 *
 * ⚠️ Endpoint `/products/bulk-update/` CHƯA TỒN TẠI (yêu cầu gửi BE
 * 19/09/2026). Hook vẫn dựng sẵn để màn hình hoàn chỉnh ngay khi BE ship —
 * và để hợp đồng nằm trong code thay vì chỉ trong một file note.
 *
 * KHÔNG nuốt lỗi 404 ở đây: phía gọi cần phân biệt "chưa lên" với "gọi hỏng"
 * để hiện đúng câu, nên chỉ chuẩn hoá message rồi ném tiếp.
 */
export function useBulkUpdateProducts() {
  const productsService = useProductsService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (data: BulkUpdateProductsRequest) => productsService.bulkUpdate(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.products.lists() });
      // Danh mục đổi thì số đếm sản phẩm theo danh mục cũng đổi.
      queryClient.invalidateQueries({ queryKey: queryKeys.categories.all });

      const failed = res?.summary?.failed ?? res?.failed?.length ?? 0;
      const updated = res?.summary?.updated ?? res?.updated?.length ?? 0;
      const unchanged = res?.summary?.unchanged ?? res?.unchanged?.length ?? 0;
      // `unchanged` là TẬP CON của `updated` — số thực sự đổi là hiệu hai cái.
      // Cộng lại là đếm trùng, và sẽ báo "đã đổi 60" cho một lượt đổi 40.
      const changed = Math.max(updated - unchanged, 0);

      // Bốn nhánh, cố ý. Báo "thành công" khi có dòng hỏng là kiểu lỗi tệ nhất
      // ở thao tác hàng loạt — người dùng đóng modal và tin rằng cả lô đã xong.
      if (failed > 0 && updated > 0) {
        toast.error(t('bulkCategoryPartial', { updated, failed }), { duration: 6000 });
      } else if (failed > 0) {
        toast.error(t('bulkCategoryAllFailed', { failed }), { duration: 6000 });
      } else if (changed === 0 && unchanged > 0) {
        // Tất cả vốn đã đúng danh mục. KHÔNG báo "đã cập nhật N" — không có gì
        // được ghi, và Audit Logs cũng sẽ trống cho lượt này.
        toast.success(t('bulkCategoryNothingToChange', { count: unchanged }));
      } else {
        toast.success(
          unchanged > 0
            ? t('bulkCategoryUpdatedSome', { changed, unchanged })
            : t('bulkCategoryUpdated', { count: changed })
        );
      }
    },
  });
}

export function useBulkCreateProducts() {
  const productsService = useProductsService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (products: CreateProductRequest[]) => productsService.bulkCreate(products),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.products.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
      toast.success(t('bulkProductsCreated', { count: String(created?.length ?? 0) }));
    },
    onError: (error: unknown) => {
      const raw = (error as { originalError?: { response?: { data?: { message?: string } } } })
        ?.originalError?.response?.data?.message;
      const lines = raw
        ? parseBackendError(raw)
        : [(error as { message?: string })?.message ?? t('failedToCreateProduct')];
      lines.forEach((line) => toast.error(line, { duration: 5000 }));
    },
  });
}

/**
 * Bản dịch của một sản phẩm (mục #15).
 *
 * Chỉ bật khi thật sự mở tab Bản dịch: sản phẩm nào cũng gọi thêm một request
 * là lãng phí, vì màn danh sách và form chính không dùng dữ liệu này (tên đã
 * được server resolve theo Accept-Language).
 */
export function useProductTranslations(productId: string | null | undefined, enabled = true) {
  const productsService = useProductsService();

  return useQuery({
    queryKey: [...queryKeys.products.all, 'translations', productId] as const,
    queryFn: () => productsService.getTranslations(productId as string),
    enabled: !!productId && enabled,
  });
}

export function useUpsertProductTranslations() {
  const productsService = useProductsService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: ({
      productId,
      translations,
    }: {
      productId: string;
      translations: ProductTranslation[];
    }) => productsService.upsertTranslations(productId, translations),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.products.all, 'translations', variables.productId],
      });
      // Danh sách hiển thị tên đã resolve theo ngôn ngữ hiện tại — sửa bản dịch
      // thì tên trên bảng cũng đổi.
      queryClient.invalidateQueries({ queryKey: queryKeys.products.lists() });
      toast.success(t('translationsSaved'));
    },
    onError: (error: unknown) => {
      toast.error((error as { message?: string })?.message ?? t('translationsSaveFailed'));
    },
  });
}

/**
 * Hook to update a product
 */
export function useUpdateProduct() {
  const productsService = useProductsService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProductRequest }) =>
      productsService.update(id, data),
    onSuccess: (updatedProduct) => {
      // Update cache
      queryClient.setQueryData(queryKeys.products.detail(updatedProduct.id), updatedProduct);
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: queryKeys.products.lists() });
      toast.success(t('toastProductUpdated', { name: updatedProduct.name }));
    },
    onError: (error: unknown) => {
      const raw = (error as { originalError?: { response?: { data?: { message?: string } } } })
        ?.originalError?.response?.data?.message;
      const lines = raw ? parseBackendError(raw) : [(error as { message?: string })?.message ?? t('failedToUpdateProduct')];
      lines.forEach((line) => toast.error(line, { duration: 5000 }));
    },
  });
}

/**
 * Hook to delete a product
 */
export function useDeleteProduct() {
  const productsService = useProductsService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (id: string) => productsService.delete(id),
    onSuccess: (_, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: queryKeys.products.detail(deletedId) });
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: queryKeys.products.lists() });
      // Invalidate inventory (product deletion affects inventory)
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
      toast.success(t('toastProductDeleted'));
    },
  });
}

/**
 * Hook to update product price
 */
export function useUpdateProductPrice() {
  const productsService = useProductsService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: ({ id, price, costPrice }: { id: string; price: number; costPrice?: number }) =>
      productsService.patch(id, {
        price: String(price),
        cost_price: costPrice !== undefined ? String(costPrice) : undefined,
      }),
    onSuccess: (updatedProduct: Product) => {
      // Update cache
      queryClient.setQueryData(queryKeys.products.detail(updatedProduct.id), updatedProduct);
      // Invalidate lists (price may affect sorting/filtering)
      queryClient.invalidateQueries({ queryKey: queryKeys.products.lists() });
      toast.success(t('toastProductPriceUpdated', { name: updatedProduct.name }));
    },
  });
}
